import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LayoutItemProps, TemplateItemProps } from "apis";
import { PageItemProps } from "apis/page";
import { PageBuilderItemProps, PageBuilderTemplateItemProps } from "apis/page-builder";
import { uuid } from "utils";
import { PaginationProps } from "utils/types/API";

interface BuilderApiItemProps {
    method?: "get" | "post" | "put" | "delete";
    url?: string;
}
export interface PageBuilderState {
    uid: string;
    loading: boolean;
    page?: PageItemProps;
    builder?: {
        layout?: LayoutItemProps;
        templates?: PageBuilderTemplateItemProps[];
    };
    history: PageBuilderItemProps[];
    saved?: boolean;
    _pagination?: PaginationProps;
    _selectTemplate?: PageBuilderTemplateItemProps;
    _tab?: 1 | 2 | 3 | 4;
    _showPropertiesTab?: boolean;
    _data?: { [key: string]: any };
}

const initialState: PageBuilderState = {
    uid: uuid(),
    loading: false,
    history: [],
    _tab: 1,
    _data: {}
};

interface IndexProps {
    data: PageBuilderItemProps[];
    refresh?: boolean;
    pagination?: PaginationProps;
}

interface LayoutChangeProps {
    layout: LayoutItemProps;
}
interface UpdateTemplates {
    // uid: string;
    templates: PageBuilderTemplateItemProps[]
}

export const pageBuilderSlice = createSlice({
    name: "page-builder",
    initialState,
    reducers: {
        loading: (state) => {
            state.loading = true;
        },
        init: (state, action: PayloadAction<PageBuilderState>) => {
            if (state.page?.id && action.payload.page?.id === state.page?.id) {
                state.uid = uuid();
                state.saved = true;
            }
            state.loading = false;
            state.page = action.payload.page;
            state.builder = action.payload.builder;
            state.history = action.payload.history;
            state._tab = action.payload.builder?.layout ? 2 : 1;
            state._showPropertiesTab = !action.payload.builder?.layout ? true : state._showPropertiesTab;
            state._pagination = action.payload._pagination;
        },
        destroy: (state) => {
            state.loading = false;
            state.page = undefined;
            state.builder = undefined;
            state.history = [];
            state.saved = undefined;
            state._pagination = undefined;
            state._selectTemplate = undefined;
            state._showPropertiesTab = undefined;
            state._tab = undefined;
            state._data = {};
        },
        layoutChange: (state, action: PayloadAction<LayoutChangeProps>) => {
            state.builder = { ...state.builder, layout: action.payload.layout };
            state.saved = false;
        },
        history: (state, action: PayloadAction<IndexProps>) => {
            state._pagination = action.payload.pagination;
            if (action.payload.refresh) {
                state.history = action.payload.data;
            }
            else {
                state.history = [...state.history, ...action.payload.data];
            }
        },
        addTemplate: (state, action: PayloadAction<PageBuilderTemplateItemProps>) => {
            state.builder = {
                ...state.builder,
                templates: [
                    ...(state.builder?.templates || []),
                    action.payload
                ].sort((a, b) => a.index - b.index)
            }
            state.saved = false;
        },
        updateTemplates: (state, action: PayloadAction<UpdateTemplates>) => {
            state.builder = {
                ...state.builder,
                templates: [
                    ...action.payload.templates
                ].sort((a, b) => a.index - b.index),
            }
            state._selectTemplate = state._selectTemplate && action.payload.templates.findIndex(t => t.itemUid === state._selectTemplate?.itemUid) >= 0 ? state._selectTemplate : undefined;
            state.saved = false;
        },
        removeTemplate: (state, action: PayloadAction<PageBuilderTemplateItemProps>) => {
            if (state._selectTemplate?.itemUid === action.payload.itemUid) {
                state._selectTemplate = undefined;
            }
            state.builder = {
                ...state.builder,
                templates: (state.builder?.templates || [])
                    .filter(temp => temp.itemUid !== action.payload.itemUid)
                    .map((temp, index) => ({ ...temp, index }))
                    .sort((a, b) => a.index - b.index)
            }
            state._selectTemplate = state._selectTemplate && (state.builder?.templates || []).findIndex(t => t.itemUid === state._selectTemplate?.itemUid) >= 0 ? state._selectTemplate : undefined;
            state.saved = false;
        },
        selectTemplate: (state, action: PayloadAction<PageBuilderTemplateItemProps>) => {
            if (Boolean(state._selectTemplate) && state._selectTemplate?.itemUid === action.payload.itemUid) {
                state._selectTemplate = undefined;
            }
            else {
                state._selectTemplate = action.payload;
                state._tab = 3;
                state._showPropertiesTab = true;
            }
        },
        updateSelectTemplate: (state, action: PayloadAction<PageBuilderTemplateItemProps>) => {
            state._selectTemplate = action.payload;
        },
        onChangeTab: (state, action: PayloadAction<1 | 2 | 3 | 4>) => {
            state._tab = action.payload;
        },
        onShowPropertiesTab: (state, action: PayloadAction<boolean>) => {
            state._showPropertiesTab = action.payload;
        }
    },
});

export const { loading, init, destroy, history, layoutChange, addTemplate, updateTemplates, removeTemplate, selectTemplate, updateSelectTemplate, onChangeTab, onShowPropertiesTab } = pageBuilderSlice.actions;
export default pageBuilderSlice.reducer;
